import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import { cx } from "shared/utils/classnames";

export interface TemplateMenuItem {
  id: string;
  name: string;
  isDefault?: boolean;
  isCurrent?: boolean;
  isNewVersion?: boolean;
  disabled?: boolean;
}

interface TemplateMenuContentProps {
  sectionTitle?: string;
  items: TemplateMenuItem[];
  otherItems?: TemplateMenuItem[];
  showAll: boolean;
  onShowAll: () => void;
  onSelect: (id: string) => void;
  className?: string;
}

const TemplateMenuItemRow = ({
  item,
  onSelect
}: {
  item: TemplateMenuItem;
  onSelect: (id: string) => void;
}) => (
  <button
    type="button"
    className={cx(
      "w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left",
      item.disabled ? "cursor-default" : "cursor-pointer hover:bg-alpha-40"
    )}
    onClick={() => !item.disabled && onSelect(item.id)}
  >
    <span className="text-sm font-medium text-content-1000 truncate min-w-0">
      {item.name}
    </span>
    <div className="flex items-center gap-1 shrink-0">
      <Show if={Boolean(item.isDefault)}>
        <Badge
          variant={BadgeVariant.BASE}
          size={BadgeSize.SM}
          style={{ mixBlendMode: "multiply" }}
        >
          Default
        </Badge>
      </Show>
      <Show if={Boolean(item.isCurrent)}>
        <Badge variant={BadgeVariant.PRIMARY} size={BadgeSize.SM}>
          Current
        </Badge>
      </Show>
      <Show if={Boolean(item.isNewVersion)}>
        <Badge variant={BadgeVariant.PRIMARY} size={BadgeSize.SM}>
          New version
        </Badge>
      </Show>
    </div>
  </button>
);

export const TemplateMenuContent = ({
  sectionTitle,
  items,
  otherItems = [],
  showAll,
  onShowAll,
  onSelect,
  className
}: TemplateMenuContentProps) => (
  <div className={cx("bg-background-1000 py-1", className)}>
    <Show if={Boolean(sectionTitle)}>
      <div className="px-4 pt-1 pb-2">
        <span className="text-xs text-content-200">{sectionTitle}</span>
        <div className="border-b border-alpha-40 mt-2" />
      </div>
    </Show>

    {items.map((item) => (
      <TemplateMenuItemRow key={item.id} item={item} onSelect={onSelect} />
    ))}

    <Show if={otherItems.length > 0 && !showAll}>
      <div className="px-4 py-2">
        <Button
          variant={ButtonVariant.TEXT}
          size={ButtonSize.SM}
          onClick={onShowAll}
        >
          Show all
        </Button>
      </div>
    </Show>

    <Show if={otherItems.length > 0 && showAll}>
      <div className="px-4 pt-3 pb-2">
        <span className="text-xs text-content-200">Other templates</span>
        <div className="border-b border-alpha-40 mt-2" />
      </div>
      {otherItems.map((item) => (
        <TemplateMenuItemRow key={item.id} item={item} onSelect={onSelect} />
      ))}
    </Show>
  </div>
);
